import type { TideResponse } from '@boatnav/schemas';

const W = 360;
const H = 80;
const PAD = 6;

/**
 * 36 h water-level curve — a plain inline SVG polyline (spec 12), no chart
 * library, DOM only so it renders in the ES5/legacy build. Tokens only:
 * `--contour` for the zero grid line, `--ink` for the curve, `--accent` for
 * the "now" marker.
 */
export function TideCurve({ series, nowMs }: { series: TideResponse['series']; nowMs: number }): JSX.Element | null {
  const pred = series.filter((p) => p.kind === 'pred').slice().sort((a, b) => a.ts - b.ts);
  if (pred.length < 2) return null;

  const minTs = pred[0]!.ts;
  const maxTs = pred[pred.length - 1]!.ts;
  const tsSpan = Math.max(1, maxTs - minTs);

  let minCm = pred[0]!.cm;
  let maxCm = pred[0]!.cm;
  for (const p of pred) {
    if (p.cm < minCm) minCm = p.cm;
    if (p.cm > maxCm) maxCm = p.cm;
  }
  const cmSpan = Math.max(1, maxCm - minCm);

  const x = (ts: number): number => PAD + ((ts - minTs) / tsSpan) * (W - 2 * PAD);
  const y = (cm: number): number => H - PAD - ((cm - minCm) / cmSpan) * (H - 2 * PAD);

  const points = pred.map((p) => `${x(p.ts).toFixed(1)},${y(p.cm).toFixed(1)}`).join(' ');
  const clampedNow = Math.min(maxTs, Math.max(minTs, nowMs));
  const nowX = x(clampedNow);
  const zeroY = minCm <= 0 && maxCm >= 0 ? y(0) : null;

  return (
    <svg className="tide-curve" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-hidden="true">
      {zeroY != null && (
        <line x1={PAD} y1={zeroY} x2={W - PAD} y2={zeroY} className="tide-curve__grid" />
      )}
      <polyline points={points} className="tide-curve__line" fill="none" />
      <line x1={nowX} y1={PAD} x2={nowX} y2={H - PAD} className="tide-curve__now" />
    </svg>
  );
}
